import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router';
import Spinner from '../Spinner';
import { useApi } from '../../api/ApiProvider';
import Api from '../../axiosApi/api';
import { useToastMessageQueue } from '../ToastMessages/ToastMessageProvider';
import { getUserEntity } from 'common/UserEntityProvider';
import intl from 'components/i18n/ReactIntlWrapper';
import InvoicesNavbar from '../common/InvoicesNavbar/InvoicesNavbar';
import InvoicesListAccountsPayable from './InvoicesListAccountsPayable';
import { handleAPIError } from 'common/errorHandler';
import { formatDateFilterInvoices } from '../../common/utils';
import ErrorSummary from 'components/common/ErrorSummary';
import { DateRange, ENTITY_TYPE } from 'common/constants';
import Tabs from '@mui/material/Tabs';
import Tab from '@mui/material/Tab';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import Button from '@mui/material/Button';
import AddIcon from '@mui/icons-material/Add';
import { GridSortModel } from '@mui/x-data-grid';
import { Card } from '@mui/material';
import { usePaginationPreference } from 'hooks/usePaginationPreference';

export type InvoicesInput = {
  to: string | null;
  number: string | null;
  date: string | null;
  from: string | null;
  range: DateRange | undefined;
  amount: number | null;
  amounts: object;
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type ValidatingInput = {
  idInvoiceType: number | null;
  idWorkflowState: number | null;
  queryTerm?: string | null;
  currentPage?: number;
  sortValue?: string | null;
  orderValue?: string | null;
};

export type SearchResult = {
  totalRecords: number | null;
  queryResult: any;
};
interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const InvoicesAccountsPayable = () => {
  const navigate = useNavigate();
  const location = useLocation();

  // Flat primitives for "all invoices" tab — no intermediate object to cause cascades
  const [allQueryTerm, setAllQueryTerm] = useState<string>('');
  const [allIdWorkflowState, setAllIdWorkflowState] = useState<number | null>(null);
  const [allRange, setAllRange] = useState<DateRange | undefined>(undefined);
  const [allAmounts, setAllAmounts] = useState<object>({});

  // Flat primitives for "validating" tab
  const [valQueryTerm, setValQueryTerm] = useState<string>('');

  // Legacy query string display state (for navbar controlled inputs)
  const [validatingQueryString, setValidatingQueryString] = useState<string>('');
  const [allInvoicesQueryString, setAllInvoicesQueryString] = useState<string>('');

  const [invoicesOwner, setInvoicesOwner] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [validatingInvoices, setValidatingInvoices] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });

  const [invoiceStatus, setInvoiceStatus] = useState<SearchResult | null>({
    totalRecords: null,
    queryResult: null,
  });
  const [value, setValue] = React.useState(() => {
    return location.pathname === '/accountspayable/all' ? 1 : 0;
  });

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
  };

  const [loading, setLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState({});

  const api: Api = useApi();

  const toast = useToastMessageQueue();

  const entityType = getUserEntity().entityType;

  const [paginationModel, setPaginationModel] = usePaginationPreference('invoicesAccountsPayable', 10);
  const [validatingPaginationModel, setValidatingPaginationModel] = usePaginationPreference('invoicesValidating', 10);
  const [sortModel, setSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);
  const [validatingSortModel, setValidatingSortModel] = useState<GridSortModel>([
    { field: 'contactName', sort: 'asc' },
  ]);

  // Keep refs to non-primitive state so fetch functions always read current values
  // without needing them as useEffect deps (which would cause spurious re-fetches).
  const allRangeRef = useRef(allRange);
  allRangeRef.current = allRange;
  const allAmountsRef = useRef(allAmounts);
  allAmountsRef.current = allAmounts;
  const sortModelRef = useRef(sortModel);
  sortModelRef.current = sortModel;
  const validatingSortModelRef = useRef(validatingSortModel);
  validatingSortModelRef.current = validatingSortModel;

  // Scalar deps only — avoids spurious re-fetches from object reference changes
  const sortField = sortModel[0]?.field ?? '';
  const sortOrder = sortModel[0]?.sort ?? 'asc';
  const valSortOrder = validatingSortModel[0]?.sort ?? 'asc';

  // Increment to trigger re-fetch when search filters (range/amounts) change
  const [allSearchVersion, setAllSearchVersion] = useState(0);

  // Dep change tracking refs for logging
  const prevAllDeps = useRef({ allQueryTerm, allIdWorkflowState, allSearchVersion, page: paginationModel.page, pageSize: paginationModel.pageSize, sortField, sortOrder });
  const prevValDeps = useRef({ valQueryTerm, page: validatingPaginationModel.page, pageSize: validatingPaginationModel.pageSize, valSortOrder });

  useEffect(() => {
    const prev = prevAllDeps.current;
    const changed: string[] = [];
    if (allQueryTerm !== prev.allQueryTerm) changed.push(`allQueryTerm: "${prev.allQueryTerm}" → "${allQueryTerm}"`);
    if (allIdWorkflowState !== prev.allIdWorkflowState) changed.push(`allIdWorkflowState: ${prev.allIdWorkflowState} → ${allIdWorkflowState}`);
    if (allSearchVersion !== prev.allSearchVersion) changed.push(`allSearchVersion: ${prev.allSearchVersion} → ${allSearchVersion}`);
    if (paginationModel.page !== prev.page) changed.push(`page: ${prev.page} → ${paginationModel.page}`);
    if (paginationModel.pageSize !== prev.pageSize) changed.push(`pageSize: ${prev.pageSize} → ${paginationModel.pageSize}`);
    if (sortField !== prev.sortField) changed.push(`sortField: "${prev.sortField}" → "${sortField}"`);
    if (sortOrder !== prev.sortOrder) changed.push(`sortOrder: "${prev.sortOrder}" → "${sortOrder}"`);
    console.log('[AP allInvoices] effect fired. Changed:', changed.length ? changed : ['NOTHING (spurious)']);
    prevAllDeps.current = { allQueryTerm, allIdWorkflowState, allSearchVersion, page: paginationModel.page, pageSize: paginationModel.pageSize, sortField, sortOrder };

    fetchInvoicesOwner();
    fetchInvoicesStatus();
  }, [
    allQueryTerm,
    allIdWorkflowState,
    allSearchVersion,
    paginationModel.page,
    paginationModel.pageSize,
    sortField,
    sortOrder,
  ]);

  useEffect(() => {
    const prev = prevValDeps.current;
    const changed: string[] = [];
    if (valQueryTerm !== prev.valQueryTerm) changed.push(`valQueryTerm: "${prev.valQueryTerm}" → "${valQueryTerm}"`);
    if (validatingPaginationModel.page !== prev.page) changed.push(`page: ${prev.page} → ${validatingPaginationModel.page}`);
    if (validatingPaginationModel.pageSize !== prev.pageSize) changed.push(`pageSize: ${prev.pageSize} → ${validatingPaginationModel.pageSize}`);
    if (valSortOrder !== prev.valSortOrder) changed.push(`valSortOrder: "${prev.valSortOrder}" → "${valSortOrder}"`);
    console.log('[AP validating] effect fired. Changed:', changed.length ? changed : ['NOTHING (spurious)']);
    prevValDeps.current = { valQueryTerm, page: validatingPaginationModel.page, pageSize: validatingPaginationModel.pageSize, valSortOrder };

    fetchValidatingInvoices();
  }, [
    valQueryTerm,
    validatingPaginationModel.page,
    validatingPaginationModel.pageSize,
    valSortOrder,
  ]);

  const fetchInvoicesOwner = () => {
    setLoading(true);
    const range = allRangeRef.current;
    const amounts = allAmountsRef.current;
    const sm = sortModelRef.current;
    const f: string[] = [];
    if (allIdWorkflowState) f.push(`idworkflowstate:${allIdWorkflowState}`);
    if (range?.from) f.push(`startdate:${formatDateFilterInvoices(range.from)}`);
    if (range?.to) f.push(`enddate:${formatDateFilterInvoices(range.to)}`);
    if (Number(amounts['min'])) f.push(`amountabove:${Number(amounts['min'])}`);
    if (Number(amounts['max'])) f.push(`amountbelow:${Number(amounts['max'])}`);

    (async () => {
      try {
        const response = await api.invoiceApi.apiVversionInvoiceSearchGet(
          '1',
          allQueryTerm,
          paginationModel.page + 1,
          paginationModel.pageSize,
          sm[0]?.field || '',
          sm[0]?.sort || 'asc',
          f.length ? f : undefined,
          {}
        );
        if (response.data.data.queryResult) {
          setInvoicesOwner({
            totalRecords: response.data?.data?.totalRecords,
            queryResult: response.data?.data?.queryResult,
          });
        } else {
          setInvoicesOwner({ totalRecords: null, queryResult: null });
        }
      } catch (error) {
        handleAPIError(error, toast, errors);
        setErrors({ ...errors });
      } finally {
        setLoading(false);
      }
    })();
  };

  const fetchInvoicesStatus = async () => {
    try {
      const response = await api.workflowApi.apiVversionModuleWorkflowStatesModuleIdGet(4, '1');
      if (response.data.data.queryResult) {
        setInvoiceStatus({
          totalRecords: response.data?.data?.totalRecords,
          queryResult: response.data?.data?.queryResult,
        });
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    }
  };

  const fetchValidatingInvoices = async () => {
    try {
      setLoading(true);
      const response = await api.invoiceApi.apiVversionInvoiceSearchIncomingGet(
        '1',
        4,
        valQueryTerm,
        validatingPaginationModel.page + 1,
        validatingPaginationModel.pageSize,
        validatingSortModel[0]?.sort || 'asc'
      );
      if (response.data.data.queryResult) {
        setValidatingInvoices({
          totalRecords: response.data?.data?.totalRecords,
          queryResult: response.data?.data?.queryResult,
        });
      } else {
        setValidatingInvoices({ totalRecords: null, queryResult: null });
      }
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
    } finally {
      setLoading(false);
    }
  };

  function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
      <div
        role="tabpanel"
        hidden={value !== index}
        id={`simple-tabpanel-${index}`}
        aria-labelledby={`simple-tab-${index}`}
        {...other}
      >
        {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
      </div>
    );
  }
  function a11yProps(index: number) {
    return {
      id: `simple-tab-${index}`,
      'aria-controls': `simple-tabpanel-${index}`,
    };
  }

  const onSearch = useCallback((queryString, idStatus, range, amounts) => {
    setErrors({});
    setAllInvoicesQueryString(queryString);
    setAllQueryTerm(queryString);
    setAllIdWorkflowState(idStatus ?? null);
    setAllRange(range);
    setAllAmounts(amounts ?? {});
    setAllSearchVersion((v) => v + 1);
  }, []);

  const onSearchValidating = useCallback((queryString, idStatus, range, amounts) => {
    setErrors({});
    setValidatingQueryString(queryString);
    setValQueryTerm(queryString);
  }, []);

  const path = window.location.pathname.split('/').pop();
  const getTitle = () => {
    if (path === 'accountspayable') {
      return intl.get('mainNavigation.billing.accountsPayable');
    }
    return intl.get('mainNavigation.invoice');
  };
  return (
    <Card sx={{ m: 2 }}>
      <Box sx={{ p: 3 }}>
        <div className="card mt-4">
          <div className="card-header">
            {
              <Typography color="primary" variant="h2" component="h2" sx={{ mb: 2 }}>
                {getTitle()}
              </Typography>
            }
          </div>
          <div className="card-body">
            <Box sx={{ width: '100%' }}>
              <Box
                sx={{
                  borderBottom: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  px: 2,
                }}
              >
                <Tabs value={value} onChange={handleChange} aria-label="basic tabs example">
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <VisibilityIcon fontSize="small" />
                        {intl.get('mainNavigation.validatingInvoices')}
                      </Box>
                    }
                    {...a11yProps(0)}
                  />
                  <Tab
                    label={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <CalendarMonthIcon fontSize="small" />
                        {intl.get('mainNavigation.allInvoices')}
                      </Box>
                    }
                    {...a11yProps(1)}
                  />
                </Tabs>

                {(entityType !== ENTITY_TYPE.CORPORATE_ENTITY || path === 'invoicing') &&
                  entityType !== ENTITY_TYPE.CLIENT && (
                    <Box>
                      <Button
                        onClick={() =>
                          entityType === ENTITY_TYPE.CORPORATE_ENTITY
                            ? navigate(`/invoicing/newinvoice`)
                            : navigate(`/myInvoices/upload`)
                        }
                        sx={{ minWidth: 40, height: 40 }}
                      >
                        <AddIcon /> {intl.get('invoice.new') ? intl.get('invoice.new') : intl.get('button.new')}
                      </Button>
                    </Box>
                  )}
              </Box>

              <CustomTabPanel value={value} index={0}>
                <InvoicesNavbar
                  invoiceStatus={validatingInvoices?.queryResult}
                  allInvoices={false}
                  onSearch={onSearchValidating}
                  queryString={validatingQueryString}
                  setQueryString={setValidatingQueryString}
                />
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <div className="col-md-12">
                      <ErrorSummary errors={errors} showEverything={false} mode="warning" />
                    </div>
                    <InvoicesListAccountsPayable
                      searchResult={validatingInvoices}
                      isValidating={true}
                      loading={loading}
                      handleSave={(stateId?: number, comments?: string) => fetchValidatingInvoices()}
                      setLoading={setLoading}
                      paginationModel={validatingPaginationModel}
                      onPaginationModelChange={setValidatingPaginationModel}
                      sortModel={validatingSortModel}
                      onSortModelChange={setValidatingSortModel}
                      WorkflowModuleId={5}
                    />
                  </>
                )}
              </CustomTabPanel>

              <CustomTabPanel value={value} index={1}>
                <InvoicesNavbar
                  invoiceStatus={invoiceStatus?.queryResult}
                  onSearch={onSearch}
                  allInvoices={true}
                  queryString={allInvoicesQueryString}
                  setQueryString={setAllInvoicesQueryString}
                />
                {loading ? (
                  <Spinner />
                ) : (
                  <>
                    <div className="col-md-12">
                      <ErrorSummary errors={errors} showEverything={false} mode="warning" />
                    </div>
                    <InvoicesListAccountsPayable
                      searchResult={invoicesOwner}
                      isValidating={false}
                      loading={loading}
                      setLoading={setLoading}
                      handleSave={(stateId?: number, comments?: string) => fetchInvoicesOwner()}
                      paginationModel={paginationModel}
                      onPaginationModelChange={setPaginationModel}
                      sortModel={sortModel}
                      onSortModelChange={setSortModel}
                      WorkflowModuleId={4}
                    />
                  </>
                )}
              </CustomTabPanel>
            </Box>
          </div>
        </div>
      </Box>
    </Card>
  );
};

export default InvoicesAccountsPayable;
